import React, { useRef, useState } from 'react';
import { Loader2, Upload, CheckCircle2, AlertCircle } from 'lucide-react';
import { uploadBankStatement, UploadResult, inr } from '../data';

/**
 * Upload a bank/card statement (CSV or Excel). The server parses it, loads the rows,
 * and matches them to unverified vendor payments — the result comes back immediately.
 */
export const StatementUpload: React.FC<{ onUploaded?: () => void }> = ({ onUploaded }) => {
	const [file, setFile] = useState<File | null>(null);
	const [account, setAccount] = useState('');
	const [bank, setBank] = useState('');
	const [busy, setBusy] = useState(false);
	const [result, setResult] = useState<UploadResult | null>(null);
	const [error, setError] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const submit = async () => {
		if (!file || !account.trim()) {
			setError('Choose a file and enter the account number.');
			return;
		}
		setBusy(true);
		setError(null);
		setResult(null);
		try {
			const r = await uploadBankStatement(file, account.trim(), bank.trim() || undefined);
			setResult(r);
			onUploaded?.();
		} catch (e: any) {
			setError(e?.response?.data?.message || e?.message || 'Upload failed');
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className='bg-white rounded-lg border border-gray-200 p-5'>
			<div className='flex items-center gap-2 mb-1'>
				<Upload size={18} className='text-blue-600' />
				<h3 className='font-semibold text-gray-900'>Upload a statement</h3>
			</div>
			<p className='text-xs text-gray-400 mb-4'>
				CSV or Excel. Columns are auto-detected (date, debit/credit, reference). We load it and
				match against unverified vendor payments straight away.
			</p>

			<div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
				<div className='md:col-span-1'>
					<label className='text-xs text-gray-500'>Account number</label>
					<input
						value={account}
						onChange={e => setAccount(e.target.value)}
						placeholder='e.g. ICICI-CC-Keshav'
						className='mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200'
					/>
				</div>
				<div className='md:col-span-1'>
					<label className='text-xs text-gray-500'>Bank (optional)</label>
					<input
						value={bank}
						onChange={e => setBank(e.target.value)}
						placeholder='ICICI'
						className='mt-1 w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200'
					/>
				</div>
				<div className='md:col-span-1'>
					<label className='text-xs text-gray-500'>File</label>
					<input
						ref={inputRef}
						type='file'
						accept='.csv,.xlsx,.xls'
						onChange={e => setFile(e.target.files?.[0] || null)}
						className='mt-1 w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 file:text-sm'
					/>
				</div>
			</div>

			<div className='flex items-center gap-3 mt-4'>
				<button
					onClick={submit}
					disabled={busy || !file || !account.trim()}
					className='px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium disabled:opacity-40 flex items-center gap-2'>
					{busy ? <Loader2 size={16} className='animate-spin' /> : <Upload size={16} />}
					{busy ? 'Uploading…' : 'Upload & reconcile'}
				</button>
				{file && <span className='text-xs text-gray-500'>{file.name}</span>}
			</div>

			{error && (
				<div className='mt-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2'>
					<AlertCircle size={16} className='mt-0.5 shrink-0' />
					<span>{error}</span>
				</div>
			)}

			{result && (
				<div className='mt-4 p-4 rounded-lg bg-green-50 border border-green-200'>
					<div className='flex items-center gap-2 text-green-800 font-medium mb-2'>
						<CheckCircle2 size={18} />
						{result.new_payments_verified} vendor payment
						{result.new_payments_verified === 1 ? '' : 's'} newly verified
					</div>
					<div className='grid grid-cols-2 md:grid-cols-4 gap-3 text-sm'>
						<Fact label='Rows loaded' value={String(result.rows_loaded)} />
						<Fact label='Skipped' value={String(result.rows_skipped)} />
						<Fact
							label='Date range'
							value={`${result.date_range.from} → ${result.date_range.to}`}
						/>
						<Fact label='Withdrawn' value={inr(result.withdrawn)} />
					</div>
					<p className='text-xs text-gray-500 mt-2'>{result.note}</p>
					<p className='text-xs text-gray-400 mt-1'>
						Detected columns: {Object.entries(result.detected_columns)
							.filter(([, v]) => v)
							.map(([k, v]) => `${k}=${v}`)
							.join(', ')}
					</p>
				</div>
			)}
		</section>
	);
};

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
	<div>
		<div className='text-xs text-gray-500'>{label}</div>
		<div className='font-medium text-gray-900'>{value}</div>
	</div>
);
